<script lang="ts">
	/**
	 * `/Settings/Admin/Settings` — every setting the store has, rendered from the registry.
	 *
	 * This page is deliberately thin: it fetches, hands the descriptors to `SettingsForm`, and
	 * posts back what changed. Nothing here knows the name of a single setting, which is the
	 * property that keeps adding one from being a client change (Design §4.5, D16).
	 */

	import { onMount } from 'svelte';
	import { EmptyState, PageHeader, SettingsForm } from '../../components';
	import { listSettings, resetSetting, updateSettings } from '../../AppNetwork';
	import type { SettingDescriptor } from '../../AppTypes';
	import { toastError, toastSuccess } from '../../stores/toast';

	let settings: SettingDescriptor[] = [];
	let loading: boolean = true;
	let loadError: string = '';
	let saving: boolean = false;
	let resettingKey: string = '';

	async function fnLoad(): Promise<void> {
		loading = true;
		loadError = '';
		try {
			settings = (await listSettings()).items;
		} catch (error) {
			loadError = (error as Error).message;
		} finally {
			loading = false;
		}
	}

	async function fnSave(event: CustomEvent<{ values: Record<string, unknown> }>): Promise<void> {
		saving = true;
		try {
			// One request for the whole screen: the server applies all of it or none of it.
			settings = (await updateSettings(event.detail.values)).items;
			const count = Object.keys(event.detail.values).length;
			toastSuccess(`Saved ${count} setting${count === 1 ? '' : 's'}.`);
		} catch (error) {
			toastError('Nothing was saved.', (error as Error).message);
		} finally {
			saving = false;
		}
	}

	async function fnReset(event: CustomEvent<{ key: string }>): Promise<void> {
		resettingKey = event.detail.key;
		try {
			settings = (await resetSetting(event.detail.key)).items;
			toastSuccess(`${event.detail.key} is back to its default.`);
		} catch (error) {
			toastError('Could not reset the setting.', (error as Error).message);
		} finally {
			resettingKey = '';
		}
	}

	onMount(fnLoad);
</script>

<PageHeader title="Settings">
	<svelte:fragment slot="description">
		Every knob the store has, generated from the settings registry. Changes take effect across all
		instances without a restart.
	</svelte:fragment>
	<svelte:fragment slot="help">
		Values resolve as <strong>environment override → database → code default</strong>. A setting
		locked by an environment variable is read-only here and names the variable, and a stored value
		the code no longer accepts falls back to its default and offers a Reset.
	</svelte:fragment>
</PageHeader>

{#if loading}
	<EmptyState variant="loading" title="Loading settings…" />
{:else if loadError.length > 0}
	<EmptyState variant="error" title="Settings could not be loaded." description={loadError}>
		<button class="text-primary-500 hover:underline" on:click={fnLoad}>Try again</button>
	</EmptyState>
{:else if settings.length === 0}
	<EmptyState title="This build declares no settings." />
{:else}
	<SettingsForm {settings} {saving} {resettingKey} on:save={fnSave} on:reset={fnReset} />
{/if}
